import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiLock, FiMail, FiCheck } from 'react-icons/fi';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(20, 5, 30, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: linear-gradient(145deg, #1d0e30, #2f0e45);
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  animation: ${slideIn} 0.4s ease;
  border: 1px solid rgba(255, 64, 192, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: white;
  }
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 20px;
  color: #ffffff;
  text-align: center;
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  background: linear-gradient(45deg, #ff40c0, #ffa040);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 25px;
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  flex: 1;
  background: linear-gradient(45deg, #ff40c0, #ff65d5);
  color: white;
  border: none;
  border-radius: 30px;
  padding: 15px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(255, 64, 192, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 64, 192, 0.5);
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 30px;
  padding: 15px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1rem;
  
  svg {
    color: #4eff91;
    min-width: 20px;
  }
`;

const RegisterModal = ({ onClose }) => {
  const navigate = useNavigate();
  
  const handleRegisterClick = () => {
    navigate('/register');
  };
  
  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        <ModalTitle>Unlock the Complete Experience</ModalTitle>
        <ModalText>
          You've tried the demo! Register to access the complete analysis and unlock all features.
        </ModalText>
        
        <BenefitsList>
          <BenefitItem>
            <FiCheck /> Complete AI analysis of Instagram bios
          </BenefitItem>
          <BenefitItem>
            <FiCheck /> Personalized compatibility suggestions
          </BenefitItem>
          <BenefitItem>
            <FiCheck /> Save analyses for future reference
          </BenefitItem>
          <BenefitItem>
            <FiCheck /> Access to exclusive premium features
          </BenefitItem>
        </BenefitsList>
        
        <ButtonsContainer>
          <PrimaryButton onClick={handleRegisterClick}>
            <FiUser /> Register for free
          </PrimaryButton>
          <SecondaryButton onClick={handleLoginClick}>
            <FiLock /> Log in
          </SecondaryButton>
        </ButtonsContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RegisterModal; 